/**
 * Single source of truth for runtime configuration.
 *
 * DivAuctions runs every flavour against one Firebase project, so the old
 * DEVELOPMENT / STAGING / PRODUCTION triplicate served no purpose and was an
 * active liability: the deployed panel selected the PRODUCTION branch while
 * only DEVELOPMENT secrets existed, so every value silently resolved to "" and
 * Algolia called https://-1.algolianet.com with blank credentials.
 *
 * Create React App inlines `process.env.REACT_APP_*` at BUILD time by literal
 * text substitution, so each name has to be written out in full below - it
 * cannot be looked up dynamically.
 *
 * The legacy `*_DEVELOPMENT_*` names are kept as a fallback so existing local
 * .env files and CI secrets keep working; prefer the flat names for anything new.
 */

const firstOf = (...values: Array<string | undefined>): string =>
  values.find((value) => value !== undefined && value !== '') ?? ''

export const FIREBASE_CONFIG = {
  appId: firstOf(
    process.env.REACT_APP_PUBLIC_APP_ID,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_APP_ID
  ),
  apiKey: firstOf(
    process.env.REACT_APP_PUBLIC_API_KEY,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_API_KEY
  ),
  authDomain: firstOf(
    process.env.REACT_APP_PUBLIC_AUTH_DOMAIN,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_AUTH_DOMAIN
  ),
  databaseURL: firstOf(
    process.env.REACT_APP_PUBLIC_DATABASE_URL,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_DATABASE_URL
  ),
  projectId: firstOf(
    process.env.REACT_APP_PUBLIC_PROJECT_ID,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_PROJECT_ID
  ),
  storageBucket: firstOf(
    process.env.REACT_APP_PUBLIC_STORAGE_BUCKET,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_STORAGE_BUCKET
  ),
  messagingSenderId: firstOf(
    process.env.REACT_APP_PUBLIC_MESSAGING_SENDER_ID,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_MESSAGING_SENDER_ID
  )
}

export const ALGOLIA_CONFIG = {
  applicationID: firstOf(
    process.env.REACT_APP_PUBLIC_ALGOLIA_APPLICATION_ID,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_ALGOLIA_APPLICATION_ID
  ),
  APIKey: firstOf(
    process.env.REACT_APP_PUBLIC_ALGOLIA_API_KEY,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_ALGOLIA_API_KEY
  )
}

export const DRIVE_CONFIG = {
  apiKey: FIREBASE_CONFIG.apiKey,
  clientId: firstOf(
    process.env.REACT_APP_PUBLIC_DRIVE_CLIENT_ID,
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_DRIVE_CLIENT_ID
  )
}

export const VAPID_KEY = firstOf(
  process.env.REACT_APP_PUBLIC_VAPID_KEY,
  process.env.REACT_APP_PUBLIC_DEVELOPMENT_NEXT_VAPID_KEY
)

export const USE_EMULATOR = process.env.REACT_APP_USE_EMULATOR === 'true'

/** Warn loudly at boot instead of failing later with an unhelpful network error. */
const missing = [
  ...Object.entries(FIREBASE_CONFIG).map(([k, v]) => [`firebase.${k}`, v]),
  ...Object.entries(ALGOLIA_CONFIG).map(([k, v]) => [`algolia.${k}`, v])
]
  .filter(([, value]) => !value)
  .map(([name]) => name)

if (missing.length) {
  // eslint-disable-next-line no-console
  console.error(
    `[config] Missing environment values at build time: ${missing.join(', ')}. ` +
      `These are inlined by CRA when the bundle is built, so this build is broken ` +
      `regardless of runtime environment.`
  )
}
